import React, { useMemo, useState } from "react";
import { Chip, Field, inputClass, ringStyle } from "./shared";
import { TERRITORY_COUNTRIES, territoryName } from "./territories";

// "Where can this be heard?" - Everywhere (no list), or only in the countries picked here.
// `value` is the list of two-letter country codes saved on the track; an empty list means no restriction.
export default function TerritoryPicker({ value, onChange, disabled }) {
  const selected = Array.isArray(value) ? value : [];
  const [restrict, setRestrict] = useState(selected.length > 0);
  const [search, setSearch] = useState("");
  const limited = restrict || selected.length > 0;

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? TERRITORY_COUNTRIES.filter((c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q) : TERRITORY_COUNTRIES;
  }, [search]);

  const toggle = (code) => {
    if (disabled) return;
    onChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code]);
  };

  return (
    <Field
      label="Where can this be heard?"
      hint={limited ? "Listeners in other countries will not see or play this track." : "Everyone, in every country, can listen."}
    >
      <div className="flex flex-wrap gap-2" role="group" aria-label="Where can this be heard">
        <Chip
          selected={!limited}
          disabled={disabled}
          onClick={() => { setRestrict(false); onChange([]); setSearch(""); }}
        >
          Everywhere
        </Chip>
        <Chip selected={limited} disabled={disabled} onClick={() => setRestrict(true)}>
          Only in selected countries
        </Chip>
      </div>

      {limited && (
        <div className="mt-3">
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3" aria-label="Selected countries">
              {selected.map((code) => (
                <span key={code} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-800">
                  {territoryName(code)}
                  {!disabled && (
                    <button type="button" aria-label={`Remove ${territoryName(code)}`} className="text-gray-500 hover:text-gray-900" onClick={() => toggle(code)}>
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
          {selected.length === 0 && (
            <p className="text-amber-700 text-sm mb-2">Pick at least one country, or choose Everywhere.</p>
          )}
          <input
            type="text"
            className={inputClass(false)}
            style={ringStyle}
            placeholder="Search countries"
            aria-label="Search countries"
            value={search}
            disabled={disabled}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="mt-2 max-h-44 overflow-y-auto rounded-xl border border-gray-200 divide-y divide-gray-100" role="listbox" aria-multiselectable="true">
            {shown.length === 0 && <p className="px-4 py-3 text-sm text-gray-500">No country matches your search.</p>}
            {shown.map((c) => (
              <label key={c.code} className="flex items-center gap-3 px-4 py-2 text-sm cursor-pointer hover:bg-gray-50">
                <input type="checkbox" checked={selected.includes(c.code)} disabled={disabled} onChange={() => toggle(c.code)} />
                <span>{c.name}</span>
                <span className="ml-auto text-gray-400">{c.code}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </Field>
  );
}
